import cn from 'classnames/bind'
import { toPairs } from 'ramda'
import { useCallback, useMemo, useRef } from 'react'

import { PlainDropdownSimplified } from 'app/common/ui-next/plain-dropdown'
import { useSaleTypesAsArray } from 'app/dataProviders/SaleTypesData'
import { SaleTypeId } from 'app/dataTypes/dishes/saleType'

import { OccupiedSaleTypes } from './DeletionPossibilityCheck'
import styles from './OccupiedReplaceForm.scss'


const cx = cn.bind(styles)
export const prefix = 'sale-type-' as const

const message = 'Тип продукции используется. Для замены этого типа необходимо задать другой:'
const messageMany = 'Некоторые типы продукции используются. Для замены этих типов необходимо задать другие:'

type Props = {
  occupiedSaleTypes: OccupiedSaleTypes
  onDeletionAllowed: (allowed: boolean) => void
}

export const OccupiedReplaceForm = ({
  occupiedSaleTypes,
  onDeletionAllowed,
}: Props) => {
  const selected = useRef<Record<SaleTypeId, true>>({})
  const noneEmptySelected = useRef<Record<SaleTypeId, true>>({})
  const changeDefined = useRef<Record<SaleTypeId, boolean>>({})

  const numberToChange = useMemo(() => {
    return occupiedSaleTypes.reduce((acc, [saleType, dishes]) => {
      selected.current[saleType.id] = true
      if (dishes) {
        noneEmptySelected.current[saleType.id] = true
        changeDefined.current[saleType.id] = false
      }
      return dishes ? acc + 1 : acc
    }, 0)
  }, [occupiedSaleTypes])

  const { saleTypes, loading } = useSaleTypesAsArray()
  const saleTypesAvailable = useMemo(() => {
    return saleTypes.filter(({ id }) => !selected.current[id])
  }, [saleTypes])

  const handleChangeSaleType = useCallback((id: SaleTypeId, elementId: string) => {
    if (saleTypesAvailable.find(({ id: saleTypeId }) => saleTypeId === id)) {
      const saleTypeIdTarget = elementId.replace(prefix, '')
      changeDefined.current[saleTypeIdTarget] = true
      if (!toPairs(changeDefined.current).find(([saleTypeId]) => !changeDefined.current[saleTypeId])) {
        onDeletionAllowed(true)
      }
      return
    }
    changeDefined.current[id] = false
    onDeletionAllowed(false)
  }, [onDeletionAllowed, saleTypesAvailable])

  return (
    <div className={styles.root}>
      <div className={styles.message}>{numberToChange > 1 ? messageMany : message}</div>
      {occupiedSaleTypes.map(([saleType, dishes], i) => (
        <div key={saleType.id} className={cx(styles.item, { triple: !!dishes, first: i === 0 })}>
          <div className={styles.names}>
            <span className={styles.namesTitle}>{`${saleType.title}: `}</span>
            {dishes
              ? <span>{dishes.join(', ')}</span>
              : <span className={styles.unused}>{'не используется'}</span>}
          </div>
          {dishes
            && <div className={styles.arrow}>{'→'}</div>}
          {dishes
            && <div>
              <PlainDropdownSimplified
                id={`${prefix}${saleType.id}`}
                items={saleTypesAvailable}
                loading={loading}
                className={styles.dropdown}
                itemsContainerClassName={styles.dropdownItem}
                onChange={handleChangeSaleType}
                keepSelectedElementInList
              />
            </div>}
        </div>
      ))}
    </div>
  )
}
